// src/features/chales/components/ChaletList.jsx
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "components/ui/dialog";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Textarea } from "components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import toast from "react-hot-toast";

const ChaletList = ({ isOpen, onClose, chales, onUpdateReservation, selectedDate }) => {
  const { register, handleSubmit, reset, setValue, watch } = useForm({
    defaultValues: {
      phoneKey: "",
      responsibleName: "",
      cpf: "",
      address: "",
      guestList: "",
    },
  });

  const isSameDate = (d1, d2) => {
    if (!d1 || !d2) return false;
    const date1 = new Date(d1);
    const date2 = new Date(d2);
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  // Only occupied chalets with check-in on the selected date
  const checkInChales = chales.filter(
    (c) => !c.isAvailable && isSameDate(c.checkInDate, selectedDate)
  );

  // Group check-in chalets by phoneInfo
  const groupedCheckInChales = React.useMemo(() => {
    const groups = {};
    checkInChales.forEach((c) => {
      const phone = c.phoneInfo || "sem-telefone-" + c.reservationId;
      if (!groups[phone]) {
        groups[phone] = {
          phoneKey: phone,
          chaletNumbers: [c.chaletNumber],
          reservationIds: [c.reservationId],
          guestInfo: c.guestInfo,
          responsibleName: c.responsibleName || "",
          cpf: c.cpf || "",
          address: c.address || "",
          guestList: c.guestList || "",
        };
      } else {
        groups[phone].chaletNumbers.push(c.chaletNumber);
        groups[phone].reservationIds.push(c.reservationId);
      }
    });
    return Object.values(groups).sort(
      (a, b) => Math.min(...a.chaletNumbers) - Math.min(...b.chaletNumbers),
    );
  }, [checkInChales]);

  const selectedPhoneKey = watch("phoneKey");

  useEffect(() => {
    if (selectedPhoneKey) {
      const selectedGroup = groupedCheckInChales.find(
        (g) => g.phoneKey === selectedPhoneKey,
      );
      if (selectedGroup) {
        setValue("responsibleName", selectedGroup.responsibleName || "");
        setValue("cpf", selectedGroup.cpf || "");
        setValue("address", selectedGroup.address || "");
        setValue("guestList", selectedGroup.guestList || "");
      }
    }
  }, [selectedPhoneKey, groupedCheckInChales, setValue]);

  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  const onSubmit = async (data) => {
    if (!data.phoneKey) {
      toast.error("Por favor, selecione um grupo de chalés.");
      return;
    }

    try {
      const selectedGroup = groupedCheckInChales.find(
        (g) => g.phoneKey === data.phoneKey,
      );
      if (selectedGroup) {
        const updatePromises = selectedGroup.reservationIds.map((id) =>
          onUpdateReservation(id, {
            responsibleName: data.responsibleName,
            cpf: data.cpf,
            address: data.address,
            guestList: data.guestList,
          }),
        );
        await Promise.all(updatePromises);
      }
      onClose();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-full h-[100dvh] max-h-none sm:h-auto sm:max-h-[85vh] sm:max-w-[500px] p-0 border-0 sm:border rounded-none sm:rounded-lg overflow-hidden flex flex-col">
        <DialogHeader className="px-6 py-4 border-b border-slate-100 dark:border-border shrink-0">
          <DialogTitle>Lista de Hóspedes</DialogTitle>
        </DialogHeader>

        <div className="px-6 py-6 overflow-y-auto flex-1">
          <form id="chalet-list-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="phoneKey">Selecione o(s) Chalé(s)</Label>
            <Select
              onValueChange={(value) => setValue("phoneKey", value)}
              value={selectedPhoneKey}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione um grupo de chalés..." />
              </SelectTrigger>
              <SelectContent>
                {groupedCheckInChales.map((g) => (
                  <SelectItem key={g.phoneKey} value={g.phoneKey}>
                    Chalés:{" "}
                    {g.chaletNumbers
                      .sort((a, b) => a - b)
                      .map((n) => String(n).padStart(2, "0"))
                      .join(", ")}{" "}
                    - {g.guestInfo}
                  </SelectItem>
                ))}
                {groupedCheckInChales.length === 0 && (
                  <div className="p-2 text-sm text-muted-foreground text-center">
                    Nenhum check-in para esta data.
                  </div>
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="responsibleName">Nome do Responsável</Label>
            <Input
              id="responsibleName"
              {...register("responsibleName")}
              placeholder="Ex: Jose da Silva"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="cpf">CPF</Label>
              <Input
                id="cpf"
                {...register("cpf")}
                placeholder="000.000.000-00"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="address">Endereço</Label>
            <Input
              id="address"
              {...register("address")}
              placeholder="Rua, Número, Bairro, Cidade/UF"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="guestList">Nomes dos Hóspedes (um por linha)</Label>
            <Textarea
              id="guestList"
              {...register("guestList")}
              placeholder="Hospede 1&#10;Hospede 2"
              rows={5}
            />
          </div>

          </form>
        </div>
        <DialogFooter className="px-6 py-4 border-t border-slate-100 dark:border-border bg-slate-50 dark:bg-card shrink-0">
          <Button type="button" variant="outline" onClick={onClose} className="w-full sm:w-auto min-h-[44px]">
            Cancelar
          </Button>
          <Button form="chalet-list-form" type="submit" className="w-full sm:w-auto min-h-[44px]">
            Salvar Lista
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ChaletList;
